import { useEffect, useState, type ReactNode } from "react";
import Button from "../../../components/Button";
import InlineErrorDisplay from "../../../components/InlineErrorDisplay";
import { Input } from "../../../components/Input";
import { Toggle } from "../../../components/Toggle";
import { SettingCard } from "../components/SettingCard";
import { chatRereadingSettingsService } from "../services/chatRereadingSettingsService";
import type { ChatRereadingSettings } from "../services/settingsTypes";

type NumberFieldKey = Exclude<keyof ChatRereadingSettings, "enabled">;
type Drafts = Record<NumberFieldKey, string>;

interface NumberFieldConfig {
  label: string;
  description: string;
  min: number;
  max: number;
  integer: boolean;
}

/** The limits match chatRereadingSettingsSchema in the API. */
const NUMBER_FIELDS: Record<NumberFieldKey, NumberFieldConfig> = {
  minSecondsOnChat: {
    label: "Seconds on the chat, at least",
    description: "Time since the chat opened or since the user's last message. Each message from the user starts the count again.",
    min: 0, max: 3600, integer: true,
  },
  minUpwardScrollScreens: {
    label: "Upward scroll in screens, at least",
    description: "Total upward scroll, in heights of the visible chat area.",
    min: 0, max: 100, integer: false,
  },
  minRevisitedMessages: {
    label: "Revisited messages, at least",
    description: "Messages that left the screen and came back.",
    min: 1, max: 100, integer: true,
  },
  cooldownMinutes: {
    label: "Cooldown in minutes",
    description: "After a drawer shows, no drawer shows for this time.",
    min: 0, max: 43200, integer: true,
  },
  escalationResetHours: {
    label: "Escalation reset in hours",
    description: "The drawers escalate: first, second, then the third for each next time. After this many hours without a drawer, the next one is the first drawer again.",
    min: 1, max: 8760, integer: true,
  },
};

const FIELD_KEYS = Object.keys(NUMBER_FIELDS) as NumberFieldKey[];

const toDrafts = (settings: ChatRereadingSettings): Drafts =>
  Object.fromEntries(FIELD_KEYS.map((key) => [key, String(settings[key])])) as Drafts;

/** The error for a field, or null when the value is valid. */
const validate = (draft: string, { min, max, integer }: NumberFieldConfig): string | null => {
  const value = Number(draft);
  if (draft.trim() === "" || Number.isNaN(value)) return "Enter a number.";
  if (integer && !Number.isInteger(value)) return "Enter a whole number.";
  if (value < min || value > max) return `Enter a value from ${min} to ${max}.`;
  return null;
};

/** A box of conditions. `operator` shows between the children, which must be a list. */
const ConditionGroup = ({ title, operator, tone, children }: {
  title: string;
  operator: "AND" | "OR";
  tone: "blue" | "amber";
  children: ReactNode[];
}) => {
  const box = tone === "blue" ? "border-blue-300 bg-blue-50/40" : "border-amber-300 bg-amber-50/60";
  const badge = tone === "blue" ? "bg-blue-600" : "bg-amber-500";
  return (
    <div className={`border-2 rounded-lg p-3 ${box}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">{title}</div>
      {children.map((child, index) => (
        <div key={index}>
          {index > 0 && (
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 border-t border-gray-300" />
              <span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${badge}`}>{operator}</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>
          )}
          {child}
        </div>
      ))}
    </div>
  );
};

export const ChatSettingsTab = () => {
  const [saved, setSaved] = useState<ChatRereadingSettings | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applySettings = (settings: ChatRereadingSettings) => {
    setSaved(settings);
    setEnabled(settings.enabled);
    setDrafts(toDrafts(settings));
  };

  useEffect(() => {
    chatRereadingSettingsService.getSettings()
      .then(applySettings)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Could not load the settings."));
  }, []);

  if (!saved || !drafts) {
    return error ? <InlineErrorDisplay message={error} /> : <div>Loading...</div>;
  }

  const fieldErrors = Object.fromEntries(FIELD_KEYS.map((key) => [key, validate(drafts[key], NUMBER_FIELDS[key])])) as Record<NumberFieldKey, string | null>;
  const isValid = FIELD_KEYS.every((key) => fieldErrors[key] === null);
  const isDirty = enabled !== saved.enabled || FIELD_KEYS.some((key) => Number(drafts[key]) !== saved[key]);

  const handleSave = async () => {
    if (!isValid || !isDirty) return;
    const settings = {
      enabled,
      ...Object.fromEntries(FIELD_KEYS.map((key) => [key, Number(drafts[key])])),
    } as ChatRereadingSettings;
    setIsSaving(true);
    setError(null);
    try {
      applySettings(await chatRereadingSettingsService.saveSettings(settings));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save the settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (key: NumberFieldKey) => {
    const field = NUMBER_FIELDS[key];
    return (
      <div>
        <div className="text-sm font-medium text-gray-900">{field.label}</div>
        <p className="text-sm text-gray-500 mb-1">{field.description}</p>
        <div className="w-32">
          <Input
            value={drafts[key]}
            onChange={(value) => setDrafts({ ...drafts, [key]: value })}
            placeholder={String(saved[key])}
          />
        </div>
        {fieldErrors[key] && <p className="text-sm text-red-600 mt-1">{fieldErrors[key]}</p>}
      </div>
    );
  };

  return (
    <SettingCard
      title="Rereading intervention"
      description="When a Pro user rereads earlier messages of a chat (the chat they are in, or one opened from the history), the app asks if this is checking and offers a new chat with Ollie. The app reads these settings when a chat opens."
    >
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      <div className="flex flex-col gap-6 mt-2">
        <div className="flex items-center gap-3">
          <Toggle checked={enabled} onChange={setEnabled} />
          <span className="text-sm font-medium text-gray-900">{enabled ? "On" : "Off: the drawer never shows"}</span>
        </div>

        <section>
          <h3 className="text-base font-bold text-gray-900">When the drawer shows</h3>
          <p className="text-sm text-gray-500 mb-3">
            The drawer shows when the time condition <strong>and</strong> one of the reading conditions are true.
            It opens on the user's next scroll, never while the user is not moving.
            The reading conditions count only messages before the user's last message and Ollie's reply to it.
            In a chat opened from the history, all messages count until the user sends a message.
          </p>
          <ConditionGroup title="All of these" operator="AND" tone="blue">
            {[
              renderField("minSecondsOnChat"),
              <ConditionGroup key="reading" title="One of these" operator="OR" tone="amber">
                {[renderField("minUpwardScrollScreens"), renderField("minRevisitedMessages")]}
              </ConditionGroup>,
            ]}
          </ConditionGroup>
        </section>

        <section>
          <h3 className="text-base font-bold text-gray-900">After a drawer shows</h3>
          <p className="text-sm text-gray-500 mb-3">These limit how often the drawer shows and which drawer shows.</p>
          <div className="flex flex-col gap-4">
            {renderField("cooldownMinutes")}
            {renderField("escalationResetHours")}
          </div>
        </section>

        <div>
          <Button onClick={handleSave} disabled={!isValid || !isDirty} loading={isSaving}>
            Save
          </Button>
        </div>
      </div>
    </SettingCard>
  );
};
